"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { IoLockClosed, IoPersonOutline, IoCallOutline, IoCheckmarkCircle, IoTimeOutline, IoCalendarOutline } from "react-icons/io5";
import { createConsultationOrder, verifyConsultationPayment } from "@/lib/consultationClient";
import { getPublicPricing } from "@/lib/consultationApiClient";

const KRISH_PRICING_ID = "123e4567-e89b-12d3-a456-426614174000";

type KrishPackage = {
  id: string;
  duration: number;
  amount: number;
  currency: string;
  label: string;
  desc: string;
};

const loadRazorpay = () =>
  new Promise<boolean>((resolve) => {
    if ((window as any).Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

export default function KrishCheckoutClient() {
  const router = useRouter();
  const searchParams = useSearchParams();
  
  const [selectedPkg, setSelectedPkg] = useState<KrishPackage | null>(null);
  const [isPricingLoading, setIsPricingLoading] = useState(true);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Capture UTM and other tracking params
  const [trackingParams, setTrackingParams] = useState<Record<string, string>>({});

  useEffect(() => {
    const params: Record<string, string> = {};
    if (searchParams) {
      searchParams.forEach((value, key) => {
        params[key] = value;
      });
    }
    setTrackingParams(params);
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;

    const loadPackage = async () => {
      try {
        const pricing = await getPublicPricing();
        const data = pricing.find((p) => p.id === KRISH_PRICING_ID) || pricing[0];

        if (cancelled) return;

        if (!data) {
          setError("The Professor Krish consultation is temporarily unavailable. Please try again shortly.");
        } else {
          setSelectedPkg({
            id: data.id,
            duration: Number(data.duration_minutes),
            amount: Number(data.amount),
            currency: data.currency || "INR",
            label: data.label || "30-Min Private Consultation",
            desc: data.description || "Private one-on-one consultation with Professor Krish.",
          });
        }
      } catch {
        if (!cancelled) {
          setError("The Professor Krish consultation is temporarily unavailable. Please try again shortly.");
        }
      }

      setIsPricingLoading(false);
    };

    void loadPackage();
    return () => {
      cancelled = true;
    };
  }, []);

  const handlePayNow = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPkg) {
      setError("The consultation package is unavailable. Please try again shortly.");
      return;
    }
    if (!name || !phone) {
      setError("Please provide your name and phone number.");
      return;
    }
    if (phone.length < 10) {
      setError("Please provide a valid 10-digit phone number.");
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // 1. Create Consultation Order
      const order = await createConsultationOrder({
        consultation: {
          name,
          contact: phone,
          email: "",
          durationMinutes: selectedPkg.duration,
          pricingId: selectedPkg.id,
          pricing_id: selectedPkg.id,
          source: trackingParams.source || trackingParams.utm_source || "website",
          sourceLabel: "celebrity_krish_lp",
          concern: "General",
          concernDetail: `Booking for ${selectedPkg.duration} minutes`,
          // Pass all tracking parameters
          utmSource: trackingParams.utm_source,
          utmMedium: trackingParams.utm_medium,
          utmCampaign: trackingParams.utm_campaign,
          utmContent: trackingParams.utm_content,
          utmTerm: trackingParams.utm_term,
          fbclid: trackingParams.fbclid,
          gclid: trackingParams.gclid,
          campaignId: trackingParams.campaign_id,
          adsetId: trackingParams.adset_id,
          adId: trackingParams.ad_id,
        },
      });

      // 2. Load Razorpay
      const razorpayReady = await loadRazorpay();
      if (!razorpayReady || !(window as any).Razorpay) {
        throw new Error("Payment gateway failed to load. Please try again.");
      }

      // 3. Initiate Razorpay Checkout
      const rzp = new (window as any).Razorpay({
        key: order.razorpay.key,
        amount: order.razorpay.amount,
        currency: order.razorpay.currency,
        name: "Professor Krish Consultation",
        description: `${selectedPkg.duration}-Min KP Astrology Reading`,
        order_id: order.razorpay.orderId,
        prefill: {
          name: name,
          contact: phone,
        },
        theme: { color: "#ea580c" }, // Orange theme for Razorpay in light mode
        handler: async (response: any) => {
          try {
            await verifyConsultationPayment({
              consultationId: order.consultationId,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            // Redirect to generic success page
            router.push("/consultations/success");
          } catch (err) {
            console.error("Verification failed:", err);
            setError("Payment successful, but verification failed. Support will contact you shortly.");
            setIsProcessing(false);
          }
        },
        modal: {
          ondismiss: () => {
            setIsProcessing(false);
          },
        },
      });

      rzp.on("payment.failed", (response: any) => {
        console.error("Payment failed", response.error);
        setError(`Payment failed: ${response.error.description}`);
        setIsProcessing(false);
      });

      rzp.open();
    } catch (err: any) {
      console.error("Payment initiation error:", err);
      setError(err.message || "Failed to start payment. Please try again.");
      setIsProcessing(false);
    }
  };

  return (
    <div className="w-full max-w-xl mx-auto relative z-10" id="booking-form">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="bg-white border border-gray-200 p-6 md:p-8 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.12)]"
      >
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-2 tracking-tight">Secure Your Private Reading</h3>
          <p className="text-orange-600 font-semibold text-sm">
            Exclusive 1-on-1 session • Very limited slots
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-xl text-sm mb-6 text-center">
            {error}
          </div>
        )}

        <form onSubmit={handlePayNow} className="space-y-6">
          
          {/* Package Selection */}
          <div className="space-y-3">
            <label className="block text-gray-900 font-bold mb-1 ml-1 text-lg">Your Consultation</label>
            <div className="grid grid-cols-1 gap-3">
              {(selectedPkg ? [selectedPkg] : []).map((pkg) => {
                const isSelected = selectedPkg?.id === pkg.id;
                return (
                  <div
                    key={pkg.id}
                    className={`relative rounded-2xl p-4 border-2 transition-all ${
                      isSelected 
                        ? "border-orange-500 bg-orange-50 shadow-md scale-[1.02]" 
                        : "border-gray-200 bg-white hover:border-orange-300 hover:bg-orange-50/50"
                    }`}
                  >
                    {isSelected && (
                      <div className="absolute -top-2 -right-2 bg-orange-500 text-white rounded-full p-1 shadow-sm">
                        <IoCheckmarkCircle className="text-lg" />
                      </div>
                    )}
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-bold text-gray-900">{pkg.label}</span>
                    </div>
                    <div className="font-extrabold text-orange-600 text-xl mb-1">
                      ₹{pkg.amount.toLocaleString('en-IN')}
                    </div>
                    <p className="text-xs text-gray-600 font-medium leading-tight">
                      {pkg.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="h-px bg-gray-200 w-full my-4"></div>

          {/* User Details */}
          <div className="space-y-4">
            <label className="block text-gray-900 font-bold mb-1 ml-1 text-lg">Your Details</label>
            
            <div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  <IoPersonOutline className="text-lg" />
                </div>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name"
                  required
                  className="w-full bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 rounded-xl py-3 pl-11 pr-4 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all font-medium"
                />
              </div>
            </div>

            <div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  <IoCallOutline className="text-lg" />
                </div>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="WhatsApp Number (e.g. 9876543210)"
                  required
                  className="w-full bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 rounded-xl py-3 pl-11 pr-4 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all font-medium"
                />
              </div>
            </div>
          </div>

          {/* Checkout Steps Info */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 my-2">
            <h4 className="text-sm font-bold text-blue-900 mb-3 flex items-center">
              What happens next?
            </h4>
            <div className="flex items-start gap-3 mb-2">
              <div className="w-6 h-6 rounded-full bg-blue-200 text-blue-700 flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">1</div>
              <p className="text-sm text-blue-800 font-medium">Complete your secure payment below.</p>
            </div>
            <div className="flex items-start gap-3 mb-2">
              <div className="w-6 h-6 rounded-full bg-blue-200 text-blue-700 flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">2</div>
              <p className="text-sm text-blue-800 font-medium">Our team contacts you within <strong className="text-blue-900">48 hours</strong>.</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-200 text-blue-700 flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">3</div>
              <p className="text-sm text-blue-800 font-medium">Your private session with Prof. Krish is scheduled.</p>
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={isProcessing || isPricingLoading || !selectedPkg}
              className="group relative w-full flex items-center justify-center bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold py-4 px-8 rounded-xl overflow-hidden transition-all hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <div className="absolute inset-0 w-full h-full bg-white/20 scale-x-0 group-hover:scale-x-100 transform origin-left transition-transform duration-300 ease-out"></div>
              
              {isProcessing || isPricingLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                  {isPricingLoading ? "Loading consultation..." : "Processing Securely..."}
                </>
              ) : selectedPkg ? (
                <>
                  <IoLockClosed className="mr-2 text-lg relative z-10" /> 
                  <span className="tracking-wide relative z-10 text-lg">PAY ₹{selectedPkg.amount.toLocaleString('en-IN')} NOW</span>
                </>
              ) : null}
            </button>
          </div>
          
          <p className="text-center text-xs text-gray-500 mt-2 flex items-center justify-center font-medium">
            <IoLockClosed className="mr-1 text-gray-400" /> 100% Secure Encrypted Payment
          </p>
        </form>
      </motion.div>
    </div>
  );
}
